export type PagePerformanceLifecycleKind = 'default' | 'list' | 'chat'

export type PagePerformanceListener = () => void

export const PAGE_PERFORMANCE_INTERACTIVE_FALLBACK_MS = 15_000

export type PagePerformanceFallbackScheduler = {
  setTimeout: (listener: PagePerformanceListener, timeoutMs: number) => number
  clearTimeout: (timerId: number) => void
}

const browserScheduler: PagePerformanceFallbackScheduler = {
  setTimeout: (listener, timeoutMs) => window.setTimeout(listener, timeoutMs),
  clearTimeout: (timerId) => window.clearTimeout(timerId),
}

const getBrowserScheduler = (): PagePerformanceFallbackScheduler | null =>
  typeof window === 'undefined' ? null : browserScheduler

const emptyUnsubscribe = () => undefined

/** 页面生命周期的通用阶段协议，不绑定具体消费任务。 */
export abstract class PagePerformanceLifecycle {
  private readonly visibleListeners = new Set<PagePerformanceListener>()
  private readonly interactiveListeners = new Set<PagePerformanceListener>()
  private interactiveFallbackScheduler: PagePerformanceFallbackScheduler | null =
    null
  private interactiveFallbackTimerId: number | null = null
  private disposed = false
  private _visible = false
  private _interactive = false

  protected constructor(
    readonly id: string,
    readonly kind: PagePerformanceLifecycleKind,
    scheduler = getBrowserScheduler(),
    interactiveFallbackMs = PAGE_PERFORMANCE_INTERACTIVE_FALLBACK_MS
  ) {
    if (scheduler) {
      this.interactiveFallbackScheduler = scheduler
      this.interactiveFallbackTimerId = scheduler.setTimeout(() => {
        this.markInteractive()
      }, interactiveFallbackMs)
    }
  }

  get visible(): boolean {
    return this._visible
  }

  get interactive(): boolean {
    return this._interactive
  }

  readonly subscribeVisible = (
    listener: PagePerformanceListener
  ): (() => void) => {
    if (this.disposed) return emptyUnsubscribe
    if (this._visible) {
      listener()
      return emptyUnsubscribe
    }
    this.visibleListeners.add(listener)
    return () => {
      this.visibleListeners.delete(listener)
    }
  }

  readonly subscribeInteractive = (
    listener: PagePerformanceListener
  ): (() => void) => {
    if (this.disposed) return emptyUnsubscribe
    if (this._interactive) {
      listener()
      return emptyUnsubscribe
    }
    this.interactiveListeners.add(listener)
    return () => {
      this.interactiveListeners.delete(listener)
    }
  }

  protected setVisible(): void {
    if (this.disposed || this._visible) return

    this._visible = true
    const listeners = Array.from(this.visibleListeners)
    this.visibleListeners.clear()
    listeners.forEach((listener) => listener())
  }

  protected setInteractive(): void {
    if (this.disposed || this._interactive) return

    this.cancelInteractiveFallback()
    this.setVisible()
    this._interactive = true
    const listeners = Array.from(this.interactiveListeners)
    this.interactiveListeners.clear()
    listeners.forEach((listener) => listener())
  }

  /** 页面专属终态或通用 fail-safe 均通过同一幂等入口推进。 */
  markInteractive(): void {
    this.setInteractive()
  }

  dispose(): void {
    if (this.disposed) return
    this.cancelInteractiveFallback()
    this.disposed = true
    this.visibleListeners.clear()
    this.interactiveListeners.clear()
  }

  private cancelInteractiveFallback(): void {
    if (
      this.interactiveFallbackTimerId === null ||
      !this.interactiveFallbackScheduler
    ) {
      return
    }

    const timerId = this.interactiveFallbackTimerId
    const scheduler = this.interactiveFallbackScheduler
    this.interactiveFallbackTimerId = null
    this.interactiveFallbackScheduler = null
    scheduler.clearTimeout(timerId)
  }
}
